/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

  // Fetch VAPID public key from edge function
  useEffect(() => {
    const fetchVapidKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-vapid-key');
        if (!error && data?.vapidPublicKey) {
          setVapidPublicKey(data.vapidPublicKey);
          console.log('[Push] VAPID key loaded');
        } else {
          console.error('[Push] Failed to fetch VAPID key:', error);
        }
      } catch (err) {
        console.error('[Push] Error fetching VAPID key:', err);
      }
    };
    fetchVapidKey();
  }, []);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    console.log('[Push] Browser support:', supported);
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check current subscription status
  const checkSubscription = useCallback(async () => {
    if (!isSupported) {
      setIsLoading(false);
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();
      
      if (subscription) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint)
            .maybeSingle();
          
          setIsSubscribed(!!data);
        } else {
          setIsSubscribed(false);
        }
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('[Push] Error checking subscription:', error);
      setIsSubscribed(false);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Le notifiche push non sono supportate su questo dispositivo');
      return false;
    }

    if (!vapidPublicKey) {
      toast.error('Configurazione notifiche non disponibile. Riprova tra qualche secondo.');
      return false;
    }

    // Check login first
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Devi effettuare il login per attivare le notifiche');
      return false;
    }

    setIsLoading(true);

    try {
      const isInIframe = window.self !== window.top;
      console.log('[Push] Subscribe flow - iframe:', isInIframe, 'permission:', Notification.permission);

      if (Notification.permission === 'denied') {
        toast.error('Permesso notifiche bloccato. Vai nelle impostazioni del browser e consenti le notifiche per questo sito, poi ricarica la pagina.', { duration: 8000 });
        setPermission('denied');
        setIsLoading(false);
        return false;
      }

      // Request permission
      let permissionResult: NotificationPermission;
      try {
        permissionResult = await Notification.requestPermission();
        console.log('[Push] Permission result:', permissionResult);
      } catch (permErr) {
        console.error('[Push] Permission error:', permErr);
        toast.error('Errore nella richiesta permessi. Prova ad aprire il sito direttamente nel browser.', { duration: 6000 });
        setIsLoading(false);
        return false;
      }
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error(permissionResult === 'denied' 
          ? 'Permesso notifiche bloccato. Vai nelle impostazioni del browser e consenti le notifiche per questo sito.' 
          : 'Permesso notifiche non concesso. Riprova.', 
          { duration: 6000 });
        setIsLoading(false);
        return false;
      }

      // Register service worker
      console.log('[Push] Registering service worker...');
      let registration: ServiceWorkerRegistration;
      try {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        console.log('[Push] Service worker ready');
      } catch (swErr) {
        console.error('[Push] Service worker registration failed:', swErr);
        toast.error('Errore nella registrazione del service worker. Ricarica la pagina e riprova.');
        setIsLoading(false);
        return false;
      }

      // Get or create push subscription
      const reg = registration as any;
      let subscription = await reg.pushManager.getSubscription();
      
      if (!subscription) {
        console.log('[Push] Creating new subscription...');
        try {
          const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey.buffer as ArrayBuffer
          });
          console.log('[Push] Subscription created');
        } catch (subErr) {
          console.error('[Push] Subscription creation failed:', subErr);
          toast.error('Errore nella creazione della sottoscrizione push. Riprova.');
          setIsLoading(false);
          return false;
        }
      }

      // Save subscription to database
      const subscriptionJson = subscription.toJSON();
      console.log('[Push] Saving to DB for user:', user.id);
      
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh: subscriptionJson.keys?.p256dh || '',
          auth: subscriptionJson.keys?.auth || ''
        }, {
          onConflict: 'user_id,endpoint'
        });

      if (error) {
        console.error('[Push] DB save error:', error);
        toast.error('Errore nel salvataggio. Riprova.');
        setIsLoading(false);
        return false;
      }

      console.log('[Push] Subscription saved successfully');
      setIsSubscribed(true);
      toast.success('Notifiche attivate con successo! 🔔');
      return true;
    } catch (error) {
      console.error('[Push] Unexpected error:', error);
      toast.error('Errore durante l\'attivazione delle notifiche');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidPublicKey]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      const registration: any = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove from database first
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint);
        }

        // Then unsubscribe
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      toast.success('Notifiche disattivate');
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Errore durante la disattivazione delle notifiche');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle subscription
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      return unsubscribe();
    } else {
      return subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    toggleSubscription,
    checkSubscription
  };
}
